import React from 'react';
import './MappingDetails.css';
import { ColumnMappingDetail } from '../../types/columnMappingReview';

interface MappingDetailsProps {
  mapping: ColumnMappingDetail;
}

const MappingDetails: React.FC<MappingDetailsProps> = ({ mapping }) => {
  const formatFullDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="mapping-details">
      <div className="details-grid">
        <div className="detail-section">
          <h4>Mapping Information</h4>
          <div className="detail-row">
            <span className="detail-label">ID:</span>
            <span className="detail-value">{mapping.id}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Original Column:</span>
            <span className="detail-value monospace">{mapping.original_column}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Metadata Name:</span>
            <span className="detail-value monospace">{mapping.metadata_name}</span>
          </div>
        </div>

        <div className="detail-section">
          <h4>Context</h4>
          <div className="detail-row">
            <span className="detail-label">Domain:</span>
            <span className="detail-value">{mapping.domain}</span>
          </div>
          {mapping.semantic_type && (
            <div className="detail-row">
              <span className="detail-label">Semantic Type:</span>
              <span className="detail-value">{mapping.semantic_type.replace(/_/g, ' ')}</span>
            </div>
          )}
          {mapping.data_type && (
            <div className="detail-row">
              <span className="detail-label">Data Type:</span>
              <span className="detail-value">{mapping.data_type}</span>
            </div>
          )}
          {mapping.data_format && (
            <div className="detail-row">
              <span className="detail-label">Format:</span>
              <span className="detail-value monospace">{mapping.data_format}</span>
            </div>
          )}
          {mapping.business_context && (
            <div className="detail-row full-width">
              <span className="detail-label">Business Context:</span>
              <span className="detail-value" style={{ marginTop: '4px', display: 'block' }}>
                {mapping.business_context}
              </span>
            </div>
          )}
        </div>

        {(mapping.validation_rules || mapping.relationships) && (
          <div className="detail-section">
            <h4>Advanced Properties</h4>
            
            {mapping.validation_rules && (
              <>
                <div className="detail-subsection">
                  <h5>Validation Rules</h5>
                  {mapping.validation_rules.required && (
                    <div className="detail-row">
                      <span className="detail-label">Required:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.validation_rules.unique && (
                    <div className="detail-row">
                      <span className="detail-label">Unique:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.validation_rules.pattern && (
                    <div className="detail-row">
                      <span className="detail-label">Pattern:</span>
                      <span className="detail-value monospace">{mapping.validation_rules.pattern}</span>
                    </div>
                  )}
                  {mapping.validation_rules.min_value !== undefined && (
                    <div className="detail-row">
                      <span className="detail-label">Min Value:</span>
                      <span className="detail-value">{mapping.validation_rules.min_value}</span>
                    </div>
                  )}
                  {mapping.validation_rules.max_value !== undefined && (
                    <div className="detail-row">
                      <span className="detail-label">Max Value:</span>
                      <span className="detail-value">{mapping.validation_rules.max_value}</span>
                    </div>
                  )}
                  {mapping.validation_rules.business_rule && (
                    <div className="detail-row full-width">
                      <span className="detail-label">Business Rule:</span>
                      <span className="detail-value" style={{ marginTop: '4px', display: 'block' }}>
                        {mapping.validation_rules.business_rule}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {mapping.relationships && (
              <>
                <div className="detail-subsection">
                  <h5>Relationships</h5>
                  {mapping.relationships.primary_key && (
                    <div className="detail-row">
                      <span className="detail-label">Primary Key:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.relationships.foreign_key && (
                    <div className="detail-row">
                      <span className="detail-label">Foreign Key:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.relationships.temporal_key && (
                    <div className="detail-row">
                      <span className="detail-label">Temporal Key:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.relationships.calculation_field && (
                    <div className="detail-row">
                      <span className="detail-label">Calculation Field:</span>
                      <span className="detail-value">Yes</span>
                    </div>
                  )}
                  {mapping.relationships.related_to && mapping.relationships.related_to.length > 0 && (
                    <div className="detail-row full-width">
                      <span className="detail-label">Related To:</span>
                      <span className="detail-value" style={{ marginTop: '4px', display: 'block' }}>
                        {mapping.relationships.related_to.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {mapping.sample_values && mapping.sample_values.length > 0 && (
          <div className="detail-section">
            <h4>Sample Values</h4>
            <div className="sample-values">
              {mapping.sample_values.slice(0, 5).map((value, index) => (
                <div key={index} className="sample-value">
                  <code>{JSON.stringify(value)}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {mapping.task_relevance && Object.keys(mapping.task_relevance).length > 0 && (
          <div className="detail-section">
            <h4>Task Relevance</h4>
            {Object.entries(mapping.task_relevance).map(([taskType, relevance]) => (
              <div key={taskType} className="task-relevance-item">
                <h5>{taskType.replace(/_/g, ' ')}</h5>
                {relevance.importance && (
                  <div className="detail-row">
                    <span className="detail-label">Importance:</span>
                    <span className="detail-value">{relevance.importance}</span>
                  </div>
                )}
                {relevance.usage && (
                  <div className="detail-row">
                    <span className="detail-label">Usage:</span>
                    <span className="detail-value">{relevance.usage}</span>
                  </div>
                )}
                {relevance.requirement && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Requirement:</span>
                    <span className="detail-value" style={{ marginTop: '4px', display: 'block' }}>
                      {relevance.requirement}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="detail-section">
          <h4>Usage Statistics</h4>
          <div className="detail-row">
            <span className="detail-label">Total Usage Count:</span>
            <span className="detail-value">{mapping.usage_count >= 99 ? '99+' : mapping.usage_count}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Last Used:</span>
            <span className="detail-value">{formatFullDate(mapping.last_used)}</span>
          </div>
          {mapping.usage_count === 0 && (
            <div className="detail-warning">
              ⚠️ This mapping has never been used in any workflow
            </div>
          )}
        </div>

        <div className="detail-section">
          <h4>Audit Information</h4>
          <div className="detail-row">
            <span className="detail-label">Created At:</span>
            <span className="detail-value">{formatFullDate(mapping.created_at)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Last Modified:</span>
            <span className="detail-value">{formatFullDate(mapping.updated_at)}</span>
          </div>
        </div>
      </div>

      <div className="details-insights">
        <h4>Insights</h4>
        {mapping.usage_count >= 99 && (
          <div className="insight high-usage">
            ✓ Frequently used mapping - high confidence in accuracy
          </div>
        )}
        {mapping.usage_count === 0 && (
          <div className="insight never-used">
            ⚠️ Never used - consider reviewing or removing
          </div>
        )}
        {mapping.last_used && new Date(mapping.last_used) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
          <div className="insight stale">
            ⚠️ Not used in 90+ days - may be outdated
          </div>
        )}
      </div>
    </div>
  );
};

export default MappingDetails;